<template>
  <div class="system-clock d-flex align-items-center gap-1 ms-2 ms-md-3" :title="title">
    <i class="fas fa-clock flex-shrink-0" :class="backtest ? 'text-warning' : 'text-secondary'"></i>
    <span v-if="backtest" class="badge rounded-pill bg-warning text-dark small fw-semibold flex-shrink-0">BACKTEST</span>
    <span v-if="backtest && paused" class="badge rounded-pill bg-dark text-warning small fw-semibold flex-shrink-0"><i class="fas fa-pause me-1"></i>PAUSED</span>
    <span class="clock-text small fw-semibold text-secondary">{{ display }}</span>
  </div>
</template>

<script>
import { fetchSystemClock } from '../services/api';

// Poll often enough to catch backtest clock JUMPS (session boundaries), and
// tick locally each second in between using real elapsed time — at 1x the
// virtual clock advances at real speed, so this stays accurate until the next
// poll corrects any jump.
const POLL_MS = 5000;
const TICK_MS = 1000;

// Full format for wide viewports; a compact one for phones so the clock never
// pushes past the header edge (weekday/year/seconds dropped).
const FORMAT_OPTS = {
  timeZone: 'America/New_York',
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
};
const COMPACT_FORMAT_OPTS = {
  timeZone: 'America/New_York',
  month: 'short', day: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: true,
};

// Match Bootstrap's `sm` breakpoint: below 576px is a phone layout.
const COMPACT_MEDIA_QUERY = '(max-width: 575.98px)';

export default {
  name: 'SystemClock',
  data() {
    return {
      serverUtcMs: null,   // backend-reported time at last poll (epoch ms, UTC)
      anchorPerfMs: null,  // performance.now() captured at that poll
      backtest: false,
      paused: false,   // the backtest-driver holds the replay paused (clock frozen)
      display: '—',
      title: '',
      ok: false,
      pollTimer: null,
      tickTimer: null,
      compact: false,
      mediaQueryList: null,
      handleMediaChange: null,
    };
  },
  mounted() {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mediaQueryList = window.matchMedia(COMPACT_MEDIA_QUERY);
      this.compact = this.mediaQueryList.matches;
      this.handleMediaChange = (event) => {
        this.compact = event.matches;
        this.render();
      };
      this.mediaQueryList.addEventListener('change', this.handleMediaChange);
    }
    this.poll();
    this.pollTimer = setInterval(this.poll, POLL_MS);
    this.tickTimer = setInterval(this.render, TICK_MS);
  },
  beforeUnmount() {
    clearInterval(this.pollTimer);
    clearInterval(this.tickTimer);
    if (this.mediaQueryList && this.handleMediaChange) {
      this.mediaQueryList.removeEventListener('change', this.handleMediaChange);
    }
  },
  methods: {
    async poll() {
      try {
        const data = await fetchSystemClock();
        const parsed = Date.parse(data.utc);
        if (Number.isNaN(parsed)) throw new Error('bad utc');
        this.serverUtcMs = parsed;
        this.anchorPerfMs = performance.now();
        this.backtest = data.backtest === true;
        this.paused = data.paused === true;
        this.ok = true;
        this.render();
      } catch (err) {
        this.ok = false;
        this.display = 'clock n/a';
        console.warn('system_clock.fetch_failed:', err.message);
      }
    },
    render() {
      if (!this.ok || this.serverUtcMs == null) return;
      // While the driver holds the replay paused the virtual clock is frozen,
      // so do not advance it locally between polls.
      const elapsed = this.paused ? 0 : performance.now() - this.anchorPerfMs;
      const d = new Date(this.serverUtcMs + elapsed);
      const opts = this.compact ? COMPACT_FORMAT_OPTS : FORMAT_OPTS;
      this.display = `${new Intl.DateTimeFormat('en-US', opts).format(d)} ET`;
      this.title = this.backtest
        ? (this.paused
          ? 'Backtest virtual clock — PAUSED: the replay is frozen at this instant'
          : 'Backtest virtual clock — the system time the stack is replaying')
        : 'System time';
    },
  },
};
</script>

<style scoped>
/* Keep the clock from being squeezed to nothing: never shrink below its content
   so, when the header row runs out of room on a phone, the whole clock wraps to
   its own line (see Header .brand-group flex-wrap) and stays fully readable. */
.system-clock {
  flex-shrink: 0;
}
.clock-text {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
